import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Check, Star, Zap, Crown, Building, ArrowRight, 
  Phone, Globe, Image, BarChart3, TrendingUp, Users,
  BadgeCheck, Sparkles
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUBSCRIPTION_TIERS = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    priceYearly: 0,
    description: 'Get discovered with a basic listing',
    color: 'slate',
    icon: Building,
    features: [
      'Business name & address',
      'Phone number display',
      'Category listing',
      'Basic search visibility',
    ],
    notIncluded: [
      'Business logo',
      'Website link',
      'Description (50 words)',
      'Featured badge',
      'Priority placement',
      'Analytics dashboard',
    ],
  },
  {
    id: 'bronze',
    name: 'Bronze',
    price: 29,
    priceYearly: 299,
    description: 'Stand out with your branding',
    color: 'amber',
    icon: Zap,
    popular: false,
    features: [
      'Everything in Free, plus:',
      'Business logo upload',
      '50-word description',
      'Website link',
      'Email display',
      'Social media links',
    ],
    notIncluded: [
      'Featured badge',
      'Priority placement',
      'Analytics dashboard',
    ],
  },
  {
    id: 'silver',
    name: 'Silver',
    price: 79,
    priceYearly: 799,
    description: 'Get featured & attract more customers',
    color: 'slate',
    icon: Star,
    popular: true,
    features: [
      'Everything in Bronze, plus:',
      'Featured badge on listing',
      'Priority category placement',
      'Photo gallery (up to 5)',
      'Customer reviews enabled',
      'Monthly performance email',
    ],
    notIncluded: [
      'Top of category placement',
      'Real-time analytics',
    ],
  },
  {
    id: 'gold',
    name: 'Gold',
    price: 149,
    priceYearly: 1499,
    description: 'Maximum visibility & insights',
    color: 'yellow',
    icon: Crown,
    features: [
      'Everything in Silver, plus:',
      'Top of category placement',
      'Homepage featured section',
      'Real-time analytics dashboard',
      'Click & enquiry tracking',
      'Competitor insights',
      'Priority support',
      'Unlimited photos',
    ],
    notIncluded: [],
  },
];

const AdvertisePage = () => {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [selectedTier, setSelectedTier] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSelectTier = async (tier) => {
    if (tier.id === 'free') {
      // Redirect to free listing form
      navigate('/hotspots/list-business?tier=free');
      return;
    }

    setSelectedTier(tier.id);
    setLoading(true);

    try {
      const price = billingCycle === 'monthly' ? tier.price : tier.priceYearly;
      const interval = billingCycle === 'monthly' ? 'month' : 'year';
      
      const response = await fetch(`${API_URL}/api/hotspots/subscriptions/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tier: tier.id,
          price: price,
          interval: interval,
          success_url: `${window.location.origin}/hotspots/subscription-success?tier=${tier.id}`,
          cancel_url: `${window.location.origin}/hotspots/advertise`,
        }),
      });

      const data = await response.json();
      
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setLoading(false);
      setSelectedTier(null);
    }
  };

  const getColorClasses = (color, type) => {
    const colors = {
      slate: {
        bg: 'bg-slate-100',
        border: 'border-slate-200',
        text: 'text-slate-600',
        button: 'bg-slate-600 hover:bg-slate-700',
        icon: 'text-slate-500',
      },
      amber: {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        text: 'text-amber-700',
        button: 'bg-amber-600 hover:bg-amber-700',
        icon: 'text-amber-500',
      },
      yellow: {
        bg: 'bg-yellow-50',
        border: 'border-yellow-300',
        text: 'text-yellow-700',
        button: 'bg-yellow-500 hover:bg-yellow-600',
        icon: 'text-yellow-500',
      },
    };
    return colors[color]?.[type] || colors.slate[type];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-orange-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-sky-600 via-sky-700 to-sky-800 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
            <Sparkles className="h-4 w-4 text-yellow-300" />
            <span className="text-sm font-medium">Grow Your Business</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            Advertise on{' '}
            <span className="text-orange-400">Australian Hotspots</span>
          </h1>
          
          <p className="text-xl text-sky-100 max-w-3xl mx-auto mb-8">
            Get discovered by thousands of customers searching for businesses like yours.
            Choose the plan that's right for your business.
          </p>

          <div className="flex flex-wrap justify-center gap-8 text-center">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl px-6 py-4">
              <div className="text-3xl font-bold">10</div>
              <div className="text-sm text-sky-200">Major Cities</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl px-6 py-4">
              <div className="text-3xl font-bold">50K+</div>
              <div className="text-sm text-sky-200">Monthly Visitors</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl px-6 py-4">
              <div className="text-3xl font-bold">24</div>
              <div className="text-sm text-sky-200">Categories</div>
            </div>
          </div>
        </div>
      </section>

      {/* Billing Toggle */}
      <section className="py-8 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-4">
            <span className={`font-medium ${billingCycle === 'monthly' ? 'text-sky-600' : 'text-slate-500'}`}>
              Monthly
            </span>
            <button
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
              className="relative w-14 h-7 bg-sky-100 rounded-full transition-colors"
            >
              <div className={`absolute top-1 w-5 h-5 bg-sky-600 rounded-full transition-all ${
                billingCycle === 'yearly' ? 'left-8' : 'left-1'
              }`} />
            </button>
            <span className={`font-medium ${billingCycle === 'yearly' ? 'text-sky-600' : 'text-slate-500'}`}>
              Yearly
            </span>
            <span className="ml-2 bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded-full">
              Save 15%
            </span>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SUBSCRIPTION_TIERS.map((tier) => {
              const Icon = tier.icon;
              const price = billingCycle === 'monthly' ? tier.price : tier.priceYearly;
              const isPopular = tier.popular;
              
              return (
                <div
                  key={tier.id}
                  className={`relative bg-white rounded-2xl border-2 ${
                    isPopular ? 'border-sky-500 shadow-xl shadow-sky-100' : 'border-slate-200'
                  } overflow-hidden transition-all duration-300 hover:shadow-lg`}
                >
                  {isPopular && (
                    <div className="absolute top-0 left-0 right-0 bg-sky-500 text-white text-center text-sm font-bold py-1">
                      Most Popular
                    </div>
                  )}
                  
                  <div className={`p-6 ${isPopular ? 'pt-10' : ''}`}>
                    {/* Icon & Name */}
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-12 h-12 rounded-xl ${getColorClasses(tier.color, 'bg')} flex items-center justify-center`}>
                        <Icon className={`h-6 w-6 ${getColorClasses(tier.color, 'icon')}`} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-slate-900">{tier.name}</h3>
                        <p className="text-sm text-slate-500">{tier.description}</p>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="mb-6">
                      {price === 0 ? (
                        <div className="text-4xl font-bold text-slate-900">Free</div>
                      ) : (
                        <div className="flex items-baseline gap-1">
                          <span className="text-4xl font-bold text-slate-900">${price}</span>
                          <span className="text-slate-500">/{billingCycle === 'monthly' ? 'mo' : 'yr'}</span>
                        </div>
                      )}
                    </div>

                    {/* CTA Button */}
                    <Button
                      onClick={() => handleSelectTier(tier)}
                      disabled={loading && selectedTier === tier.id}
                      className={`w-full mb-6 ${
                        isPopular 
                          ? 'bg-sky-500 hover:bg-sky-600' 
                          : tier.id === 'gold'
                            ? 'bg-yellow-500 hover:bg-yellow-600 text-slate-900'
                            : getColorClasses(tier.color, 'button')
                      } text-white`}
                      data-testid={`select-tier-${tier.id}`}
                    >
                      {loading && selectedTier === tier.id ? (
                        'Processing...'
                      ) : tier.id === 'free' ? (
                        <>List for Free <ArrowRight className="ml-2 h-4 w-4" /></>
                      ) : (
                        <>Get Started <ArrowRight className="ml-2 h-4 w-4" /></>
                      )}
                    </Button>

                    {/* Features */}
                    <ul className="space-y-3">
                      {tier.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-slate-600">{feature}</span>
                        </li>
                      ))}
                      {tier.notIncluded.map((feature, index) => (
                        <li key={`not-${index}`} className="flex items-start gap-2 opacity-50">
                          <div className="h-5 w-5 flex-shrink-0 mt-0.5 flex items-center justify-center">
                            <div className="w-1.5 h-0.5 bg-slate-400 rounded" />
                          </div>
                          <span className="text-sm text-slate-400 line-through">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Why Advertise With Us?
            </h2>
            <p className="text-lg text-slate-600">
              Join hundreds of businesses growing with Australian Hotspots
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-sky-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-sky-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Reach More Customers</h3>
              <p className="text-slate-600">
                Get discovered by locals and tourists actively searching for businesses in your area.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Boost Your SEO</h3>
              <p className="text-slate-600">
                Our high-authority domain helps your business rank higher in Google searches.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Track Your Results</h3>
              <p className="text-slate-600">
                See exactly how many people view and contact your business with our analytics.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-2">Can I upgrade or downgrade my plan?</h3>
              <p className="text-slate-600">Yes! You can change your plan at any time. Upgrades take effect immediately, and downgrades apply at your next billing cycle.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-2">Is there a contract or commitment?</h3>
              <p className="text-slate-600">No long-term contracts. Monthly plans can be cancelled anytime. Yearly plans offer savings but are billed annually.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-2">How long until my listing goes live?</h3>
              <p className="text-slate-600">Free listings are reviewed within 24-48 hours. Paid listings go live immediately after payment confirmation.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-2">Can I list in multiple categories?</h3>
              <p className="text-slate-600">Yes! Your primary category is included. Additional categories can be added for $10/month each.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-orange-500 to-orange-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Grow Your Business?
          </h2>
          <p className="text-xl text-orange-100 mb-8">
            Join thousands of Australian businesses already listed on Australian Hotspots
          </p>
          <Button
            size="lg"
            className="bg-white text-orange-600 hover:bg-orange-50"
            onClick={() => window.scrollTo({ top: 400, behavior: 'smooth' })}
          >
            View Pricing Plans
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Lionheart Digital Branding - POWERFUL */}
          <div className="flex flex-col items-center text-center mb-10">
            <div className="relative mb-6">
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 to-red-500/20 blur-3xl rounded-full"></div>
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/28a7e231-ff1b-4b8f-915f-47798802b078/images/2fa9ddf7953c616448f48ed011e225e9e3469523a5a5c8f53c562308f1d829cc.png" 
                alt="Lionheart Digital" 
                className="h-28 md:h-36 w-auto relative z-10 drop-shadow-2xl"
              />
            </div>
            <h3 className="text-2xl md:text-3xl lg:text-4xl font-black tracking-tight mb-3">
              <span className="text-white">LIONHEART</span>
              <span className="text-orange-500"> DIGITAL</span>
            </h3>
            <p className="text-lg md:text-xl font-bold italic text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-red-500 to-orange-400">
              "They lit the fire. We became it."
            </p>
          </div>
          <div className="border-t border-slate-800 pt-6 text-center">
            <Link to="/hotspots" className="text-xl font-bold">
              <span className="text-sky-400">Australian</span>
              <span className="text-orange-400"> Hotspots</span>
            </Link>
            <p className="text-slate-400 text-sm mt-4">
              &copy; {new Date().getFullYear()} Australian Hotspots. A Lionheart Digital Asset.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AdvertisePage;
